import { ZfIcon, ZfIconButton, ZfStack } from '@zafire/react';
import moment from 'moment/moment';
import type { CSSProperties, FunctionComponent } from 'react';
import { useMemo, useRef } from 'react';
import { TimelineBar } from './TimelineBar';
import { TimelineItem } from './TimelineItem';
import type { IEventTimelineItem } from './timelineLayout';
import { COLUMN_WIDTH, panelHeight, todayLeft, yearLeft } from './timelineLayout';

export type { IEventTimelineItem } from './timelineLayout';

interface IProps {
    startYear: number;
    endYear: number;
    birthYear?: number;
    items: IEventTimelineItem[];
}

export const EventTimeline: FunctionComponent<IProps> = ({ startYear, endYear, birthYear, items }) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const years = useMemo(() => Array.from({ length: Math.max(0, endYear - startYear + 1) }, (_, index) => startYear + index), [startYear, endYear]);

    const now = moment();
    const todayOffset = todayLeft(startYear, endYear, now);
    // Left edge of the current year's column; the elapsed part (up to the today line) is shaded as past too.
    const currentYearLeft = todayOffset != null ? yearLeft(now.year(), startYear) : undefined;

    const scrollByColumns = (direction: 'left' | 'right') => {
        const columns = direction === 'left' ? -3 : 3;
        scrollRef.current?.scrollBy({ left: columns * COLUMN_WIDTH, behavior: 'smooth' });
    };

    const rootStyle = { '--event-col-width': `${COLUMN_WIDTH}px`, '--event-timeline-height': `${panelHeight(items)}px` } as CSSProperties;

    return (
        <div className="event-timeline" style={rootStyle}>
            <div className="event-timeline__scroll" ref={scrollRef}>
                <TimelineBar years={years} birthYear={birthYear} todayLeft={todayOffset} />

                <ZfStack direction="row" className="event-timeline__panel">
                    <div className="event-timeline__column event-timeline__column--past event-timeline__column--lead" />
                    {years.map((year) => (
                        <div className={`event-timeline__column${year < now.year() ? ' event-timeline__column--past' : ''}`} key={year} />
                    ))}

                    {todayOffset != null && currentYearLeft != null && (
                        <div className="event-timeline__past-fill" style={{ left: `${currentYearLeft}px`, width: `${todayOffset - currentYearLeft}px` }} />
                    )}

                    {todayOffset != null && <div className="event-timeline__today" style={{ left: `${todayOffset}px` }} />}

                    {items
                        .filter((item) => item.year >= startYear && item.year <= endYear)
                        .map((item) => (
                            <TimelineItem key={item.key} item={item} startYear={startYear} />
                        ))}
                </ZfStack>
            </div>

            <ZfStack direction="row" justify="space-between" className="event-timeline__nav">
                <ZfIconButton hierarchy="secondary" onClick={() => scrollByColumns('left')}>
                    <ZfIcon name="keyboard_arrow_left" />
                </ZfIconButton>
                <ZfIconButton hierarchy="secondary" onClick={() => scrollByColumns('right')}>
                    <ZfIcon name="keyboard_arrow_right" />
                </ZfIconButton>
            </ZfStack>
        </div>
    );
};
